import React from 'react';
import {
  Text,
  TextDate,
  Price,
  Spacer,
} from 'components';
import { useI18n } from 'components/I18n';
import ChevronIcon from 'components/Icons/ChevronIcon';
import { DATE_FORMAT } from 'utils/constants';
import {
  TableRow,
  TableCell,
  SubCell,
  CellHeader,
  CellTitle,
  Separator,
  ToggleButton,
} from './Styles';
import TransactionPeriod from './TransactionPeriod';
import { TTransaction } from './types';
import { getDiscountTextId } from './utils';

interface ITransactionProps {
  transaction: TTransaction;
  withHeader: boolean;
}

function Transaction({ transaction, withHeader }: ITransactionProps) {
  const i18n = useI18n();
  const [isExpanded, setIsExpanded] = React.useState(false);

  const toggleExpanded = () => setIsExpanded(prevIsExpanded => !prevIsExpanded);

  const showHeader = withHeader && isExpanded;

  return (
    <>
      <TableRow expanded={isExpanded}>
        <TableCell expanded={isExpanded}>
          {showHeader && (
            <CellHeader>
              <Text id="transaction.date" />
            </CellHeader>
          )}
          <TextDate value={transaction.timestamp} options={DATE_FORMAT} />
        </TableCell>
        <TableCell expanded={isExpanded}>
          {showHeader && (
            <CellHeader>
              <Text id="transaction.offer" />
            </CellHeader>
          )}
          <span>{transaction.offerV3?.title}</span>
        </TableCell>
        <TableCell expanded={isExpanded}>
          {showHeader && (
            <CellHeader>
              <Text id="transaction.status" />
            </CellHeader>
          )}
          <Text id={`transactionStatus.${transaction.status.toLowerCase()}`} />
        </TableCell>
        <TableCell expanded={isExpanded}>
          {showHeader && (
            <CellHeader>
              <Text id="transaction.price" />
            </CellHeader>
          )}
          <Text nowrap>
            <Price
              currency={transaction.currency}
              price={transaction.netPrice}
            /> *
          </Text>
        </TableCell>
        <TableCell align="right" expanded={isExpanded}>
          <ToggleButton onClick={toggleExpanded}>
            <Text
              boldWidthForTextInAttr="data-content"
              data-content={i18n.formatText(`transaction.${isExpanded ? 'hide' : 'details'}`)}
              id={`transaction.${isExpanded ? 'hide' : 'details'}`}
            />
            <Spacer width={10} />
            <ChevronIcon direction={isExpanded ? 'down' : 'right'} />
          </ToggleButton>
        </TableCell>
      </TableRow>
      {isExpanded && (
        <>
          <TableRow expanded>
            <td colSpan={5}>
              <Separator />
            </td>
          </TableRow>
          <TableRow expanded>
            <TableCell expanded />
            <TableCell expanded>
              <SubCell>
                <CellTitle>
                  <Text id="transaction.period" />
                </CellTitle>
                <TransactionPeriod transaction={transaction} />
              </SubCell>
            </TableCell>
            <TableCell expanded />
            <TableCell expanded>
              <SubCell>
                <CellTitle>
                  <Text id="transaction.grossPrice" />
                </CellTitle>
                <Price
                  currency={transaction.currency}
                  price={transaction.grossPrice}
                />
              </SubCell>
              <SubCell>
                <CellTitle>
                  <Text id={getDiscountTextId(transaction.transactionType)} />
                </CellTitle>
                <Price
                  currency={transaction.currency}
                  price={-(transaction.grossPrice - transaction.netPrice)}
                />
              </SubCell>
              <SubCell>
                <CellTitle>
                  <Text id="transaction.total" />
                </CellTitle>
                <Price
                  currency={transaction.currency}
                  price={transaction.netPrice}
                  fontSize="1.1em"
                />
              </SubCell>
            </TableCell>
            <TableCell expanded />
          </TableRow>
          <TableRow expanded>
            <TableCell colSpan="5" paddingTopOff>
              * <Text id="transaction.taxesIncluded" />
            </TableCell>
          </TableRow>
        </>
      )}
    </>
  );
}

export default React.memo(Transaction);
